import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  Chip,
  LinearProgress,
  Paper,
  alpha,
  useTheme,
  CircularProgress,
  Fade,
  Collapse,
  Alert,
  Tooltip,
  Slider,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
} from "@mui/material";
import {
  Close,
  Headphones,
  Download,
  ContentCopy,
  CheckCircle,
  PlayArrow,
  Refresh,
  Mic,
  VolumeUp,
  RecordVoiceOver,
  Speed,
  Tune,
  KeyboardArrowDown,
  KeyboardArrowUp,
  FiberManualRecord,
  Campaign,
  MenuBook,
  Stop,
  SpatialAudio,
  Pause,
  Radio,
  VolumeMute,
} from "@mui/icons-material";
import { generateAudiobook } from "../services/new_blogsServices";
import { withStyles } from "@material-ui/core/styles";

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: "blue",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}))(Tooltip);

const extractTitle = (pipelineContents) => {
  const sources = [
    pipelineContents?.idea,
    pipelineContents?.outline,
    pipelineContents?.cover,
    pipelineContents?.summary,
  ];
  for (const src of sources) {
    if (!src) continue;
    const m =
      src.match(/(?:book\s*title|title)\s*[:\-]\s*(.+)/i) ||
      src.match(/^(.{10,60})\n/);
    if (m?.[1]) return m[1].replace(/[*_#]/g, "").trim().substring(0, 80);
  }
  return "Untitled Book";
};

const clampChapterCount = (value) => {
  const parsed = parseInt(value, 10);
  if (Number.isNaN(parsed) || parsed < 1) return 1;
  return parsed;
};

const VOICE_PROFILES = [
  {
    id: "warm_narrator",
    label: "Warm Narrator",
    icon: "🎙️",
    description: "Friendly, inviting tone — great for general fiction",
  },
  {
    id: "dramatic_storyteller",
    label: "Dramatic Storyteller",
    icon: "🎭",
    description: "Rich, theatrical delivery — ideal for thrillers & fantasy",
  },
  {
    id: "calm_guide",
    label: "Calm Guide",
    icon: "🧘",
    description: "Soothing, measured voice — perfect for self-help & biography",
  },
  {
    id: "energetic_host",
    label: "Energetic Host",
    icon: "⚡",
    description: "Upbeat, engaging — best for adventure & YA",
  },
];

const AUDIO_SECTIONS = [
  {
    id: "intro_script",
    label: "Opening Narration",
    icon: <Campaign />,
    color: "#f59e0b",
  },
  {
    id: "chapter_scripts",
    label: "Chapter Scripts",
    icon: <MenuBook />,
    color: "#ec4899",
  },
  {
    id: "scene_directions",
    label: "Scene Directions",
    icon: <Tune />,
    color: "#3b82f6",
  },
  {
    id: "voice_notes",
    label: "Voice & Pacing Notes",
    icon: <RecordVoiceOver />,
    color: "#10b981",
  },
  {
    id: "outro_script",
    label: "Closing Narration",
    icon: <SpatialAudio />,
    color: "#8b5cf6",
  },
];

const WaveformBar = ({ delay, color, isActive }) => (
  <Box
    sx={{
      width: 3,
      borderRadius: 2,
      background: color,
      height: isActive ? "100%" : "30%",
      animation: isActive
        ? `waveAnim 0.8s ease-in-out ${delay}s infinite alternate`
        : "none",
      transition: "height 0.3s ease",
      "@keyframes waveAnim": {
        "0%": { height: "20%" },
        "100%": { height: "100%" },
      },
    }}
  />
);

const AudioWaveform = ({ color, isActive, bars = 12 }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "3px", height: 32 }}>
    {Array.from({ length: bars }).map((_, i) => (
      <WaveformBar
        key={i}
        delay={(i * 0.08) % 0.6}
        color={color}
        isActive={isActive}
      />
    ))}
  </Box>
);

const NarrationControls = ({
  sectionId,
  text,
  color,
  speakingSection,
  isSpeaking,
  isPaused,
  onPlay,
  onPause,
  onResume,
  onStop,
}) => {
  const isThisSpeaking = speakingSection === sectionId && isSpeaking;
  const isThisPaused = speakingSection === sectionId && isPaused;
  if (!text) return null;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {!isThisSpeaking && !isThisPaused ? (
        <LightTooltip title="Read aloud">
          <IconButton
            size="small"
            onClick={() => onPlay(text, sectionId)}
            sx={{
              color,
              width: 26,
              height: 26,
              "&:hover": { bgcolor: alpha(color, 0.12) },
            }}
          >
            <VolumeUp sx={{ fontSize: 15 }} />
          </IconButton>
        </LightTooltip>
      ) : (
        <>
          {isThisPaused ? (
            <LightTooltip title="Resume">
              <IconButton
                size="small"
                onClick={onResume}
                sx={{
                  color,
                  width: 26,
                  height: 26,
                  "&:hover": { bgcolor: alpha(color, 0.12) },
                }}
              >
                <PlayArrow sx={{ fontSize: 15 }} />
              </IconButton>
            </LightTooltip>
          ) : (
            <LightTooltip title="Pause">
              <IconButton
                size="small"
                onClick={onPause}
                sx={{
                  color,
                  width: 26,
                  height: 26,
                  "&:hover": { bgcolor: alpha(color, 0.12) },
                }}
              >
                <Pause sx={{ fontSize: 15 }} />
              </IconButton>
            </LightTooltip>
          )}
          <LightTooltip title="Stop">
            <IconButton
              size="small"
              onClick={onStop}
              sx={{
                color: "#ef4444",
                width: 26,
                height: 26,
                "&:hover": { bgcolor: alpha("#ef4444", 0.1) },
              }}
            >
              <Stop sx={{ fontSize: 14 }} />
            </IconButton>
          </LightTooltip>
        </>
      )}
    </Stack>
  );
};

const ScriptBlock = ({
  content,
  color,
  label,
  voiceNote,
  sectionId,
  speakingSection,
  isSpeaking,
  isPaused,
  onPlay,
  onPause,
  onResume,
  onStop,
}) => {
  const isThisSpeaking = speakingSection === sectionId && isSpeaking;
  if (!content) return null;
  return (
    <Box
      sx={{
        borderRadius: 2.5,
        overflow: "hidden",
        mb: 2,
        border: `1px solid ${alpha(color, isThisSpeaking ? 0.6 : 0.25)}`,
        background: alpha(color, isThisSpeaking ? 0.07 : 0.03),
        transition: "all 0.3s ease",
        boxShadow: isThisSpeaking
          ? `0 0 0 2px ${alpha(color, 0.2)}, 0 4px 20px ${alpha(color, 0.15)}`
          : "none",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.2,
          background: alpha(color, 0.1),
          borderBottom: `1px solid ${alpha(color, 0.15)}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Mic sx={{ fontSize: 14, color }} />
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color, letterSpacing: 0.8, fontSize: "0.7rem" }}
          >
            {label}
          </Typography>
          {isThisSpeaking && <AudioWaveform color={color} isActive bars={6} />}
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {voiceNote && (
            <Chip
              label={voiceNote}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.6rem",
                bgcolor: alpha(color, 0.15),
                color,
                border: `1px solid ${alpha(color, 0.3)}`,
              }}
            />
          )}
          <NarrationControls
            sectionId={sectionId}
            text={content}
            color={color}
            speakingSection={speakingSection}
            isSpeaking={isSpeaking}
            isPaused={isPaused}
            onPlay={onPlay}
            onPause={onPause}
            onResume={onResume}
            onStop={onStop}
          />
        </Stack>
      </Box>
      <Box sx={{ px: 2.5, py: 2 }}>
        <Typography
          variant="body2"
          sx={{
            lineHeight: 2,
            color: "text.primary",
            fontSize: "0.9rem",
            whiteSpace: "pre-wrap",
            fontFamily: "'Courier New', monospace",
            letterSpacing: 0.3,
          }}
        >
          {content}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── Chapter Script Card ──────────────────────────────────────────────────────

const ChapterScriptCard = ({
  chNum,
  title,
  script,
  color,
  isGenerating,
  sectionId,
  speakingSection,
  isSpeaking,
  isPaused,
  onPlay,
  onPause,
  onResume,
  onStop,
}) => {
  const [expanded, setExpanded] = useState(chNum === 1);
  const isThisSpeaking = speakingSection === sectionId && isSpeaking;
  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: 2.5,
        overflow: "hidden",
        border: `1px solid ${alpha(color, isThisSpeaking ? 0.5 : 0.2)}`,
        boxShadow: isThisSpeaking ? `0 0 0 2px ${alpha(color, 0.2)}` : "none",
        transition: "all 0.3s ease",
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 2,
          background: alpha(color, isThisSpeaking ? 0.12 : 0.06),
          borderBottom: expanded ? `1px solid ${alpha(color, 0.12)}` : "none",
          transition: "background 0.2s",
        }}
      >
        <Box
          onClick={() => setExpanded((e) => !e)}
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            cursor: "pointer",
            flexShrink: 0,
            background: `linear-gradient(135deg, ${color}, ${alpha(color, 0.6)})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontWeight: 800,
            fontSize: "0.75rem",
          }}
        >
          {chNum}
        </Box>
        <Box
          sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
          onClick={() => setExpanded((e) => !e)}
        >
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ fontSize: "0.85rem" }}
            noWrap
          >
            Chapter {chNum}
            {title ? `: ${title}` : ""}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: "0.68rem" }}
          >
            {script
              ? `~${Math.ceil(script.split(" ").length / 150)} min read`
              : "Generating..."}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {isThisSpeaking && <AudioWaveform color={color} isActive bars={5} />}
          {script && (
            <NarrationControls
              sectionId={sectionId}
              text={script}
              color={color}
              speakingSection={speakingSection}
              isSpeaking={isSpeaking}
              isPaused={isPaused}
              onPlay={onPlay}
              onPause={onPause}
              onResume={onResume}
              onStop={onStop}
            />
          )}
          {isGenerating && !script && (
            <CircularProgress size={14} sx={{ color }} />
          )}
          {script && !isThisSpeaking && (
            <FiberManualRecord sx={{ fontSize: 8, color: "#10b981" }} />
          )}
          <Box
            onClick={() => setExpanded((e) => !e)}
            sx={{ cursor: "pointer" }}
          >
            {expanded ? (
              <KeyboardArrowUp sx={{ fontSize: 18, color: "text.secondary" }} />
            ) : (
              <KeyboardArrowDown
                sx={{ fontSize: 18, color: "text.secondary" }}
              />
            )}
          </Box>
        </Stack>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 2.5, py: 2 }}>
          {script ? (
            <Typography
              variant="body2"
              sx={{
                lineHeight: 2,
                color: "text.primary",
                fontSize: "0.88rem",
                whiteSpace: "pre-wrap",
                fontFamily: "'Courier New', monospace",
                letterSpacing: 0.3,
              }}
            >
              {script}
            </Typography>
          ) : (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}
            >
              <CircularProgress size={16} sx={{ color }} />
              <Typography variant="caption" color="text.secondary">
                Writing narration script for Chapter {chNum}...
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const VoiceProfileCard = ({ profile, isSelected, onClick }) => {
  const theme = useTheme();
  return (
    <Card
      onClick={onClick}
      sx={{
        borderRadius: 2.5,
        cursor: "pointer",
        transition: "all 0.2s ease",
        border: isSelected
          ? `2px solid #ec4899`
          : `1px solid ${alpha(theme.palette.divider, 0.2)}`,
        background: isSelected ? alpha("#ec4899", 0.06) : "transparent",
        "&:hover": {
          borderColor: "#ec4899",
          background: alpha("#ec4899", 0.04),
          transform: "translateY(-1px)",
          boxShadow: `0 4px 16px ${alpha("#ec4899", 0.15)}`,
        },
      }}
    >
      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography sx={{ fontSize: "1.4rem" }}>{profile.icon}</Typography>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                fontSize: "0.82rem",
                color: isSelected ? "#ec4899" : "text.primary",
              }}
            >
              {profile.label}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.68rem", display: "block" }}
              noWrap
            >
              {profile.description}
            </Typography>
          </Box>
          {isSelected && (
            <CheckCircle
              sx={{ fontSize: 16, color: "#ec4899", flexShrink: 0 }}
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const PlaceholderBox = ({ color, label, isActive }) => (
  <Box sx={{ textAlign: "center", py: 6 }}>
    {isActive ? (
      <>
        <CircularProgress sx={{ color }} size={36} thickness={3} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {label}
        </Typography>
      </>
    ) : (
      <Typography
        variant="body2"
        color="text.disabled"
        sx={{ fontStyle: "italic" }}
      >
        This section will be generated when reached.
      </Typography>
    )}
  </Box>
);

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════

const BlogAIAudioBook = ({
  open,
  onClose,
  pipelineContents = {},
  generators = [],
  bookType = "",
  language = "English",
}) => {
  const theme = useTheme();

  // ── Book Settings ─────────────────────────────────────────
  const [selectedVoice, setSelectedVoice] = useState(VOICE_PROFILES[0]);
  const [narratorName, setNarratorName] = useState("");
  const [chapterCount, setChapterCount] = useState(5);
  const [bookTitle, setBookTitle] = useState("");
  const [customLanguage, setCustomLanguage] = useState(language || "English");
  const [customTone, setCustomTone] = useState("");

  // ── Generation State ──────────────────────────────────────
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [overallProgress, setOverallProgress] = useState(0);
  const [generationComplete, setGenerationComplete] = useState(false);
  const [error, setError] = useState(null);

  // ── Content ───────────────────────────────────────────────
  const [introScript, setIntroScript] = useState("");
  const [outroScript, setOutroScript] = useState("");
  const [voiceNotes, setVoiceNotes] = useState("");
  const [sceneDirections, setSceneDirections] = useState("");
  const [chapterTitles, setChapterTitles] = useState([]);
  const [chapterScripts, setChapterScripts] = useState({});

  // ── UI ────────────────────────────────────────────────────
  const [activeView, setActiveView] = useState("intro_script");
  const [copied, setCopied] = useState(false);

  // ── Audio Narration State ─────────────────────────────────
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [speakingSection, setSpeakingSection] = useState(null);
  const [speechRate, setSpeechRate] = useState(1.0);
  const [speechPitch, setSpeechPitch] = useState(1.0);
  const [browserVoices, setBrowserVoices] = useState([]);
  const [selectedBrowserVoice, setSelectedBrowserVoice] = useState(null);
  const [isRadioMode, setIsRadioMode] = useState(false);
  const [radioStatus, setRadioStatus] = useState("");

  const abortRef = useRef(false);
  const radioModeRef = useRef(false);
  const voiceRef = useRef(null);
  const rateRef = useRef(1.0);
  const pitchRef = useRef(1.0);
  const isSpeakingRef = useRef(false);
  const radioQueueRef = useRef([]);
  const currentIndexRef = useRef(0);

  useEffect(() => {
    voiceRef.current = selectedBrowserVoice;
  }, [selectedBrowserVoice]);
  useEffect(() => {
    rateRef.current = speechRate;
  }, [speechRate]);
  useEffect(() => {
    pitchRef.current = speechPitch;
  }, [speechPitch]);

  const hasAnyContent =
    !!introScript || !!voiceNotes || Object.keys(chapterScripts).length > 0;

  // ── Load browser voices ───────────────────────────────────
  const getLangCode = (lang) => {
    if (!lang) return "en";

    const map = {
      English: "en",
      Tamil: "ta",
      Hindi: "hi",
      French: "fr",
      Spanish: "es",
      German: "de",
      Japanese: "ja",
      Chinese: "zh",
    };

    return map[lang] || lang.slice(0, 2).toLowerCase();
  };
  useEffect(() => {
    const loadVoices = () => {
      const allVoices = window.speechSynthesis.getVoices();

      if (!allVoices || allVoices.length === 0) return;

      const langCode = getLangCode(customLanguage || language);
      let filteredVoices = allVoices.filter((v) =>
        v.lang.toLowerCase().startsWith(langCode),
      );
      if (filteredVoices.length === 0) {
        filteredVoices = allVoices;
      }

      setBrowserVoices(filteredVoices);

      setSelectedBrowserVoice((prev) => {
        if (prev) return prev;

        const preferred =
          filteredVoices.find((v) =>
            /natural|enhanced|premium/i.test(v.name),
          ) ||
          filteredVoices.find((v) => v.default) ||
          filteredVoices[0] ||
          null;

        voiceRef.current = preferred;
        return preferred;
      });
    };

    loadVoices();

    if (typeof window.speechSynthesis.onvoiceschanged !== "undefined") {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      window.speechSynthesis.cancel();
    };
  }, [customLanguage, language]);

  // ── Reset on dialog open ──────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const title = extractTitle(pipelineContents);
    setBookTitle(title);
    const outline = pipelineContents?.outline || "";
    const m = outline.match(/(\d+)\s*chapters?/i);
    setChapterCount(m ? clampChapterCount(m[1]) : 4);
    setCustomLanguage(language || "English");
    setCustomTone("");
    setSelectedVoice(VOICE_PROFILES[0]);
    setNarratorName("");
    setIntroScript("");
    setOutroScript("");
    setVoiceNotes("");
    setSceneDirections("");
    setChapterTitles([]);
    setChapterScripts({});
    setOverallProgress(0);
    setGenerationComplete(false);
    setCurrentStep(null);
    setActiveView("intro_script");
    setError(null);
    abortRef.current = false;
    radioModeRef.current = false;
    window.speechSynthesis.cancel();
    isSpeakingRef.current = false;
    setIsSpeaking(false);
    setIsPaused(false);
    setSpeakingSection(null);
    setIsRadioMode(false);
    setRadioStatus("");
  }, [open]); 
  const effectiveLanguage =
    (customLanguage || language || "English").trim() || "English";
  const effectiveTone =
    (customTone || selectedVoice.label || "Narrative").trim() || "Narrative";
  const effectiveVoiceProfile = selectedVoice.label || effectiveTone;

  // ── Generation ────────────────────────────────────────────

  const handleGenerate = async () => {
    try {
      setIsGenerating(true);
      setError(null);
      abortRef.current = false;
      const payload = {
        language: effectiveLanguage,
        book_title: bookTitle,
        chapter_count: clampChapterCount(chapterCount),
        tone: effectiveTone,
        voice_profile: effectiveVoiceProfile,
        narrator_name: narratorName || "Narrator",
        book_genre: bookType || "Fiction",
        idea: pipelineContents?.idea || "",
        outline: pipelineContents?.outline || "",
        summary: pipelineContents?.summary || "",
      };
      const res = await generateAudiobook(payload);
      const data = res?.data || res;
      if (!data) throw new Error("Invalid API response");
      setVoiceNotes(data.voice_notes || "");
      setIntroScript(data.intro_script || "");
      setSceneDirections(data.scene_directions || "");
      setOutroScript(data.outro_script || "");
      if (Array.isArray(data.chapter_scripts)) {
        const titles = data.chapter_scripts.map((ch, i) => ({
          num: i + 1,
          title: ch.title || `Chapter ${i + 1}`,
        }));
        const scripts = {};
        data.chapter_scripts.forEach((ch, i) => {
          scripts[i + 1] = ch.script || "";
        });
        setChapterTitles(titles);
        setChapterScripts(scripts);
      }
      setGenerationComplete(true);
      setOverallProgress(100);
    } catch (err) {
      console.error(err);
      setError("Failed to generate audiobook.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleStop = () => {
    abortRef.current = true;
    setIsGenerating(false);
    setCurrentStep(null);
  };

  const speakText = useCallback(
    (text, sectionId, onEnd) => {
      if (!text) return;

      window.speechSynthesis.cancel();

      const utter = new SpeechSynthesisUtterance(text);
      utter.voice = voiceRef.current || null;
      utter.lang =
        voiceRef.current?.lang || getLangCode(customLanguage) || "en-US";
      utter.rate = rateRef.current;
      utter.pitch = pitchRef.current;
      utter.volume = 1;

      utter.onstart = () => {
        isSpeakingRef.current = true;
        setIsSpeaking(true);
        setIsPaused(false);
        setSpeakingSection(sectionId);
      };

      utter.onend = () => {
        isSpeakingRef.current = false;
        setIsSpeaking(false);
        setIsPaused(false);
        setSpeakingSection(null);
        if (onEnd) onEnd();
      };

      utter.onerror = (e) => {
        console.error("Speech error:", e.error);
        isSpeakingRef.current = false;
        setIsSpeaking(false);
        setIsPaused(false);
        setSpeakingSection(null);
      };

      window.speechSynthesis.speak(utter);
    },
    [customLanguage],
  );

  const pauseNarration = useCallback(() => {
    if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  }, []);

  const resumeNarration = useCallback(() => {
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    }
  }, []);

  const stopNarration = useCallback(() => {
    radioModeRef.current = false;

    window.speechSynthesis.cancel();

    isSpeakingRef.current = false;
    setIsSpeaking(false);
    setIsPaused(false);
    setSpeakingSection(null);
    setIsRadioMode(false);
    setRadioStatus("Paused — resume anytime");
  }, []);

  const handleVoiceChange = useCallback((newVoice) => {
    voiceRef.current = newVoice;
    setSelectedBrowserVoice(newVoice);
    if (isSpeakingRef.current && !radioModeRef.current) {
      window.speechSynthesis.cancel();
      isSpeakingRef.current = false;
      setIsSpeaking(false);
      setIsPaused(false);
      setSpeakingSection(null);
    }
  }, []);

  const handleRateChange = useCallback((v) => {
    rateRef.current = v;
    setSpeechRate(v);
  }, []);
  const handlePitchChange = useCallback((v) => {
    pitchRef.current = v;
    setSpeechPitch(v);
  }, []);

  const playNext = (index) => {
    if (!radioModeRef.current) return;

    const queue = radioQueueRef.current;

    if (index >= queue.length) {
      radioModeRef.current = false;
      setIsRadioMode(false);
      currentIndexRef.current = 0;
      setRadioStatus("✓ Radio complete.");
      setIsSpeaking(false);
      setSpeakingSection(null);
      return;
    }

    currentIndexRef.current = index;

    const item = queue[index];

    setRadioStatus(`Now playing: ${item.label} (${index + 1}/${queue.length})`);

    setActiveView(item.id.startsWith("chapter_") ? "chapter_scripts" : item.id);

    speakText(item.text, item.id, () => {
      if (!radioModeRef.current) return;
      setTimeout(() => playNext(index + 1), 500);
    });
  };

  const startRadioMode = useCallback(() => {
    if (!hasAnyContent) return;

    if (radioQueueRef.current.length > 0 && currentIndexRef.current > 0) {
      radioModeRef.current = true;
      setIsRadioMode(true);
      playNext(currentIndexRef.current);
      return;
    }
    window.speechSynthesis.cancel();
    isSpeakingRef.current = false;
    setIsSpeaking(false);
    setIsPaused(false);
    setSpeakingSection(null);

    const queue = [];

    if (introScript) {
      queue.push({
        id: "intro_script",
        label: "Opening Narration",
        text: introScript,
      });
    }

    chapterTitles.forEach((ch) => {
      const script = chapterScripts[ch.num];
      if (script) {
        queue.push({
          id: `chapter_${ch.num}`,
          label: `Chapter ${ch.num}: ${ch.title}`,
          text: script,
        });
      }
    });

    if (sceneDirections) {
      queue.push({
        id: "scene_directions",
        label: "Scene Directions",
        text: sceneDirections,
      });
    }

    if (voiceNotes) {
      queue.push({
        id: "voice_notes",
        label: "Voice & Pacing Notes",
        text: voiceNotes,
      });
    }

    if (outroScript) {
      queue.push({
        id: "outro_script",
        label: "Closing Narration",
        text: outroScript,
      });
    }

    if (queue.length === 0) return;
    radioQueueRef.current = queue;
    currentIndexRef.current = 0;

    radioModeRef.current = true;
    setIsRadioMode(true);

    playNext(0);
  }, [
    hasAnyContent,
    introScript,
    chapterTitles,
    chapterScripts,
    sceneDirections,
    voiceNotes,
    outroScript,
  ]);

  const buildFullScript = () => {
    const parts = [
      `╔══════════════════════════════════════════╗`,
      `║   AUDIOBOOK SCRIPT: ${bookTitle.toUpperCase().substring(0, 22).padEnd(22)}   ║`,
      `║   Voice: ${effectiveVoiceProfile.padEnd(32)}   ║`,
      `╚══════════════════════════════════════════╝\n`,
    ];
    if (voiceNotes)
      parts.push("━━━ VOICE & PACING NOTES ━━━\n" + voiceNotes + "\n");
    if (introScript)
      parts.push("━━━ OPENING NARRATION ━━━\n" + introScript + "\n");
    if (sceneDirections)
      parts.push("━━━ SCENE DIRECTIONS ━━━\n" + sceneDirections + "\n");
    chapterTitles.forEach((ch) => {
      const s = chapterScripts[ch.num];
      if (s)
        parts.push(
          `━━━ CHAPTER ${ch.num}: ${ch.title.toUpperCase()} ━━━\n${s}\n`,
        );
    });
    if (outroScript)
      parts.push("━━━ CLOSING NARRATION ━━━\n" + outroScript + "\n");
    return parts.join("\n");
  };

  const handleDownload = () => {
    const text = buildFullScript();
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${bookTitle.replace(/\s+/g, "_")}_audiobook_script.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(buildFullScript()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const narrationProps = {
    speakingSection,
    isSpeaking,
    isPaused,
    onPlay: speakText,
    onPause: pauseNarration,
    onResume: resumeNarration,
    onStop: stopNarration,
  };

  const NarrationSettingsPanel = () => (
    <Box>
      <Typography
        variant="overline"
        sx={{
          fontSize: "0.58rem",
          color: "text.disabled",
          fontWeight: 700,
          letterSpacing: 1.2,
          mb: 0.8,
          display: "block",
        }}
      >
        NARRATION SETTINGS
      </Typography>

      {browserVoices.length > 0 && (
        <FormControl fullWidth size="small" sx={{ mb: 1 }}>
          <Select
            value={
              selectedBrowserVoice
                ? Math.max(0, browserVoices.indexOf(selectedBrowserVoice))
                : 0
            }
            onChange={(e) => handleVoiceChange(browserVoices[e.target.value])}
            sx={{ borderRadius: 2, fontSize: "0.72rem" }}
          >
            {browserVoices.map((v, i) => (
              <MenuItem key={i} value={i} sx={{ fontSize: "0.72rem" }}>
                {v.name.length > 26 ? v.name.substring(0, 26) + "…" : v.name}
                {v.default ? " ★" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Speed sx={{ fontSize: 12, color: "text.secondary" }} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.62rem", minWidth: 28 }}
        >
          Speed
        </Typography>
        <Slider
          value={speechRate}
          min={0.5}
          max={2}
          step={0.1}
          onChange={(_, v) => handleRateChange(v)}
          size="small"
          sx={{ color: "#ec4899", flex: 1 }}
        />
        <Typography
          variant="caption"
          sx={{ fontSize: "0.62rem", color: "#ec4899", minWidth: 28 }}
        >
          {speechRate.toFixed(1)}×
        </Typography>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <RecordVoiceOver sx={{ fontSize: 12, color: "text.secondary" }} />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.62rem", minWidth: 28 }}
        >
          Pitch
        </Typography>
        <Slider
          value={speechPitch}
          min={0.5}
          max={2}
          step={0.1}
          onChange={(_, v) => handlePitchChange(v)}
          size="small"
          sx={{ color: "#8b5cf6", flex: 1 }}
        />
        <Typography
          variant="caption"
          sx={{ fontSize: "0.62rem", color: "#8b5cf6", minWidth: 28 }}
        >
          {speechPitch.toFixed(1)}
        </Typography>
      </Stack>
    </Box>
  );

  const renderPreview = () => {
    if (activeView === "intro_script")
      return introScript ? (
        <ScriptBlock
          content={introScript}
          color="#f59e0b"
          label="OPENING NARRATION SCRIPT"
          voiceNote={effectiveTone}
          sectionId="intro_script"
          {...narrationProps}
        />
      ) : (
        <PlaceholderBox
          color="#f59e0b"
          label="Crafting opening narration..."
          isActive={currentStep === "intro_script" && isGenerating}
        />
      );

    if (activeView === "voice_notes")
      return voiceNotes ? (
        <ScriptBlock
          content={voiceNotes}
          color="#10b981"
          label="VOICE & PACING GUIDE"
          voiceNote={effectiveLanguage}
          sectionId="voice_notes"
          {...narrationProps}
        />
      ) : (
        <PlaceholderBox
          color="#10b981"
          label="Writing voice notes..."
          isActive={currentStep === "voice_notes" && isGenerating}
        />
      );

    if (activeView === "scene_directions")
      return sceneDirections ? (
        <ScriptBlock
          content={sceneDirections}
          color="#3b82f6"
          label="SCENE DIRECTION NOTES"
          sectionId="scene_directions"
          {...narrationProps}
        />
      ) : (
        <PlaceholderBox
          color="#3b82f6"
          label="Creating scene directions..."
          isActive={currentStep === "scene_directions" && isGenerating}
        />
      );

    if (activeView === "chapter_scripts") {
      if (chapterTitles.length === 0)
        return (
          <PlaceholderBox
            color="#ec4899"
            label="Preparing chapter scripts..."
            isActive={isGenerating}
          />
        );
      return (
        <Box>
          <Typography
            variant="overline"
            sx={{
              fontSize: "0.65rem",
              color: "#ec4899",
              fontWeight: 700,
              letterSpacing: 1.5,
              mb: 2,
              display: "block",
            }}
          >
            ALL CHAPTER SCRIPTS
          </Typography>
          {chapterTitles.map((ch) => (
            <ChapterScriptCard
              key={ch.num}
              chNum={ch.num}
              title={ch.title}
              script={chapterScripts[ch.num]}
              color="#ec4899"
              isGenerating={currentStep === "chapter_scripts" && isGenerating}
              sectionId={`chapter_${ch.num}`}
              {...narrationProps}
            />
          ))}
        </Box>
      );
    }

    if (activeView === "outro_script")
      return outroScript ? (
        <ScriptBlock
          content={outroScript}
          color="#8b5cf6"
          label="CLOSING NARRATION SCRIPT"
          voiceNote="Warm & reflective"
          sectionId="outro_script"
          {...narrationProps}
        />
      ) : (
        <PlaceholderBox
          color="#8b5cf6"
          label="Writing closing narration..."
          isActive={currentStep === "outro_script" && isGenerating}
        />
      );

    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={isGenerating ? undefined : onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: "95vw",
          maxWidth: 1200,
          height: "92vh",
          borderRadius: 3,
          overflow: "hidden",
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(135deg, #0f0c29, #1a1040)"
              : "linear-gradient(135deg, #fdf4ff, #f0fafb)",
          border: `1px solid ${alpha("#ec4899", 0.2)}`,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          flexShrink: 0,
          background: "linear-gradient(135deg, #1a0533, #0c1a3d)",
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderBottom: `1px solid ${alpha("#ec4899", 0.2)}`,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            flexShrink: 0,
            background: "linear-gradient(135deg, #ec4899, #8b5cf6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 16px rgba(236,72,153,0.4)",
          }}
        >
          <Headphones sx={{ color: "white", fontSize: 22 }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="subtitle1"
              fontWeight={800}
              sx={{ color: "white", fontSize: "0.95rem" }}
              noWrap
            >
              {bookTitle || "Audiobook Script Generator"}
            </Typography>
            <Chip
              label="AI · Audio"
              size="small"
              sx={{
                bgcolor: alpha("#ec4899", 0.2),
                color: "#ec4899",
                fontWeight: 700,
                fontSize: "0.6rem",
                border: `1px solid ${alpha("#ec4899", 0.4)}`,
              }}
            />
            {isRadioMode && (
              <Chip
                label="📻 Radio Mode"
                size="small"
                sx={{
                  bgcolor: alpha("#10b981", 0.2),
                  color: "#10b981",
                  fontWeight: 700,
                  fontSize: "0.6rem",
                  border: `1px solid ${alpha("#10b981", 0.4)}`,
                  animation: "radioChipPulse 1.5s ease-in-out infinite",
                  "@keyframes radioChipPulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.6 },
                  },
                }}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <AudioWaveform
              color={isSpeaking ? "#10b981" : alpha("#ec4899", 0.6)}
              isActive={isSpeaking}
              bars={10}
            />
            <Typography
              variant="caption"
              sx={{ color: alpha("#fff", 0.5), fontSize: "0.68rem" }}
              noWrap
            >
              {isRadioMode && radioStatus
                ? radioStatus
                : generationComplete
                  ? `✓ Complete — ${chapterTitles.length} chapters scripted`
                  : isGenerating
                    ? `Scripting... ${overallProgress}%`
                    : isSpeaking
                      ? isPaused
                        ? "⏸ Paused"
                        : "🔊 Narrating..."
                      : `${effectiveTone} · ${effectiveLanguage}`}
            </Typography>
          </Stack>
        </Box>

        {(isGenerating || generationComplete) && (
          <Box sx={{ width: 120, flexShrink: 0 }}>
            <LinearProgress
              variant="determinate"
              value={overallProgress}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: alpha("#fff", 0.08),
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  transition: "transform 0.6s ease",
                  background:
                    overallProgress === 100
                      ? "linear-gradient(90deg, #10b981, #8b5cf6)"
                      : "linear-gradient(90deg, #ec4899, #8b5cf6)",
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: alpha("#fff", 0.45), fontSize: "0.65rem" }}
            >
              {overallProgress}%
            </Typography>
          </Box>
        )}

        <Stack direction="row" spacing={0.5}>
          {hasAnyContent && (
            <LightTooltip
              title={
                isRadioMode ? "Stop Radio" : "Radio Mode — play all sections"
              }
            >
              <IconButton
                size="small"
                onClick={isRadioMode ? stopNarration : startRadioMode}
                sx={{
                  color: isRadioMode ? "#10b981" : alpha("#fff", 0.6),
                  "&:hover": { color: "white", bgcolor: alpha("#fff", 0.08) },
                }}
              >
                <Radio fontSize="small" />
              </IconButton>
            </LightTooltip>
          )}
          {isSpeaking && (
            <LightTooltip title="Stop narration">
              <IconButton
                size="small"
                onClick={stopNarration}
                sx={{
                  color: "#ef4444",
                  "&:hover": { bgcolor: alpha("#ef4444", 0.15) },
                }}
              >
                <VolumeMute fontSize="small" />
              </IconButton>
            </LightTooltip>
          )}
          {hasAnyContent && (
            <>
              <LightTooltip title="Copy Script">
                <IconButton
                  size="small"
                  onClick={handleCopy}
                  sx={{
                    color: copied ? "#10b981" : alpha("#fff", 0.6),
                    "&:hover": { color: "white", bgcolor: alpha("#fff", 0.08) },
                  }}
                >
                  {copied ? (
                    <CheckCircle fontSize="small" />
                  ) : (
                    <ContentCopy fontSize="small" />
                  )}
                </IconButton>
              </LightTooltip>
              <LightTooltip title="Download Script">
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  sx={{
                    color: alpha("#fff", 0.6),
                    "&:hover": { color: "white", bgcolor: alpha("#fff", 0.08) },
                  }}
                >
                  <Download fontSize="small" />
                </IconButton>
              </LightTooltip>
            </>
          )}
          {!isGenerating && (
            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: alpha("#fff", 0.5),
                "&:hover": { color: "white", bgcolor: alpha("#ef4444", 0.2) },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Box>

      {/* ── Body ───────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Left Sidebar */}
        <Box
          sx={{
            width: 250,
            flexShrink: 0,
            borderRight: `1px solid ${alpha("#ec4899", 0.1)}`,
            display: "flex",
            flexDirection: "column",
            background:
              theme.palette.mode === "dark"
                ? alpha("#0f0c29", 0.9)
                : alpha("#fdf4ff", 0.9),
            overflow: "hidden",
          }}
        >
          {/* Generate / Stop / Regenerate */}
          <Box
            sx={{ p: 2, borderBottom: `1px solid ${alpha("#ec4899", 0.1)}` }}
          >
            {!isGenerating && !generationComplete ? (
              <Button
                fullWidth
                variant="contained"
                size="small"
                startIcon={<Mic />}
                onClick={handleGenerate}
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  textTransform: "none",
                  background: "linear-gradient(135deg, #ec4899, #8b5cf6)",
                  boxShadow: "0 4px 14px rgba(236,72,153,0.4)",
                  "&:hover": { boxShadow: "0 6px 20px rgba(236,72,153,0.55)" },
                }}
              >
                Generate Script
              </Button>
            ) : isGenerating ? (
              <Button
                fullWidth
                variant="outlined"
                size="small"
                startIcon={<Stop />}
                onClick={handleStop}
                sx={{
                  borderRadius: 2,
                  fontSize: "0.78rem",
                  textTransform: "none",
                  borderColor: "#ef4444",
                  color: "#ef4444",
                  "&:hover": { bgcolor: alpha("#ef4444", 0.08) },
                }}
              >
                Stop
              </Button>
            ) : (
              <Stack spacing={0.8}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<Refresh />}
                  onClick={() => {
                    stopNarration();
                    setIntroScript("");
                    setOutroScript("");
                    setVoiceNotes("");
                    setSceneDirections("");
                    setChapterTitles([]);
                    setChapterScripts({});
                    setOverallProgress(0);
                    setGenerationComplete(false);
                    setCurrentStep(null);
                    setActiveView("intro_script");
                  }}
                  sx={{
                    borderRadius: 2,
                    fontSize: "0.78rem",
                    textTransform: "none",
                    borderColor: "#ec4899",
                    color: "#ec4899",
                  }}
                >
                  Regenerate
                </Button>
                <Button
                  fullWidth
                  variant={isRadioMode ? "contained" : "outlined"}
                  size="small"
                  startIcon={isRadioMode ? <Stop /> : <Radio />}
                  onClick={isRadioMode ? stopNarration : startRadioMode}
                  sx={{
                    borderRadius: 2,
                    fontSize: "0.78rem",
                    textTransform: "none",
                    borderColor: isRadioMode ? "#ef4444" : "#10b981",
                    color: isRadioMode ? "#fff" : "#10b981",
                    bgcolor: isRadioMode ? "#ef4444" : "transparent",
                    "&:hover": {
                      bgcolor: isRadioMode
                        ? alpha("#ef4444", 0.85)
                        : alpha("#10b981", 0.08),
                    },
                  }}
                >
                  {isRadioMode ? "Stop Radio" : "📻 Radio Mode"}
                </Button>
              </Stack>
            )}
          </Box>

          {/* Voice Profile selector — before generation */}
          {!hasAnyContent && !isGenerating && (
            <Box
              sx={{
                p: 2,
                borderBottom: `1px solid ${alpha("#ec4899", 0.1)}`,
                overflowY: "auto",
                "&::-webkit-scrollbar": { width: 4 },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: alpha("#ec4899", 0.2),
                  borderRadius: 2,
                },
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  fontSize: "0.6rem",
                  color: "text.disabled",
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  mb: 1,
                  display: "block",
                }}
              >
                VOICE PROFILE
              </Typography>
              <Stack spacing={0.8} sx={{ mb: 1.5 }}>
                {VOICE_PROFILES.map((p) => (
                  <VoiceProfileCard
                    key={p.id}
                    profile={p}
                    isSelected={selectedVoice.id === p.id}
                    onClick={() => setSelectedVoice(p)}
                  />
                ))}
              </Stack>
              <TextField
                fullWidth
                size="small"
                label="Narrator Name (optional)"
                value={narratorName}
                onChange={(e) => setNarratorName(e.target.value)}
                sx={{
                  mb: 1.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: "0.82rem",
                  },
                }}
              />
              <NarrationSettingsPanel />
            </Box>
          )}

          {/* Narration Settings — after generation */}
          {hasAnyContent && (
            <Box
              sx={{
                p: 1.5,
                borderBottom: `1px solid ${alpha("#ec4899", 0.1)}`,
              }}
            >
              <NarrationSettingsPanel />
            </Box>
          )}

          {/* Section Nav */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 1.5,
              "&::-webkit-scrollbar": { width: 4 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: alpha("#ec4899", 0.2),
                borderRadius: 2,
              },
            }}
          >
            <Typography
              variant="overline"
              sx={{
                fontSize: "0.6rem",
                color: "text.disabled",
                fontWeight: 700,
                letterSpacing: 1.2,
                px: 1,
                display: "block",
                mb: 1,
              }}
            >
              SCRIPT SECTIONS
            </Typography>
            <Stack spacing={0.5}>
              {AUDIO_SECTIONS.map((sec) => {
                const isComplete =
                  sec.id === "intro_script"
                    ? !!introScript
                    : sec.id === "outro_script"
                      ? !!outroScript
                      : sec.id === "voice_notes"
                        ? !!voiceNotes
                        : sec.id === "scene_directions"
                          ? !!sceneDirections
                          : sec.id === "chapter_scripts"
                            ? chapterTitles.length > 0
                            : false;
                const isSectionSpeaking =
                  sec.id === "chapter_scripts"
                    ? speakingSection?.startsWith("chapter_")
                    : speakingSection === sec.id;
                return (
                  <Box
                    key={sec.id}
                    onClick={() => setActiveView(sec.id)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                      background:
                        isSectionSpeaking && isSpeaking
                          ? alpha(sec.color, 0.18)
                          : activeView === sec.id
                            ? alpha(sec.color, 0.12)
                            : isComplete
                              ? alpha(sec.color, 0.05)
                              : "transparent",
                      border: `1px solid ${
                        isSectionSpeaking && isSpeaking
                          ? alpha(sec.color, 0.6)
                          : activeView === sec.id
                            ? alpha(sec.color, 0.5)
                            : isComplete
                              ? alpha(sec.color, 0.2)
                              : alpha(theme.palette.divider, 0.12)
                      }`,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        background: alpha(sec.color, 0.08),
                        borderColor: alpha(sec.color, 0.3),
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        flexShrink: 0,
                        transition: "all 0.3s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: isComplete
                          ? "linear-gradient(135deg, #10b981, #059669)"
                          : activeView === sec.id ||
                              (isSectionSpeaking && isSpeaking)
                            ? `linear-gradient(135deg, ${sec.color}, ${alpha(sec.color, 0.7)})`
                            : alpha(theme.palette.divider, 0.25),
                        color:
                          isComplete ||
                          activeView === sec.id ||
                          (isSectionSpeaking && isSpeaking)
                            ? "white"
                            : "text.disabled",
                        boxShadow:
                          isSectionSpeaking && isSpeaking
                            ? `0 0 8px ${alpha(sec.color, 0.5)}`
                            : activeView === sec.id
                              ? `0 2px 8px ${alpha(sec.color, 0.35)}`
                              : "none",
                      }}
                    >
                      {isComplete ? (
                        <CheckCircle sx={{ fontSize: 14 }} />
                      ) : (
                        React.cloneElement(sec.icon, { sx: { fontSize: 14 } })
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        fontSize: "0.78rem",
                        fontWeight:
                          activeView === sec.id ? 700 : isComplete ? 600 : 400,
                        color:
                          isSectionSpeaking && isSpeaking
                            ? sec.color
                            : activeView === sec.id
                              ? sec.color
                              : isComplete
                                ? "text.primary"
                                : "text.disabled",
                      }}
                    >
                      {sec.label}
                    </Typography>
                    {isSectionSpeaking && isSpeaking ? (
                      <AudioWaveform color={sec.color} isActive bars={4} />
                    ) : (
                      isComplete && (
                        <FiberManualRecord
                          sx={{ fontSize: 7, color: "#10b981" }}
                        />
                      )
                    )}
                  </Box>
                );
              })}

              {chapterTitles.length > 0 && (
                <Box sx={{ ml: 1.5, mt: 0.5 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: "0.55rem",
                      color: "text.disabled",
                      fontWeight: 700,
                      letterSpacing: 1,
                      px: 0.5,
                      display: "block",
                      mb: 0.5,
                    }}
                  >
                    CHAPTERS ({chapterTitles.length})
                  </Typography>
                  {chapterTitles.map((ch) => {
                    const chSectionId = `chapter_${ch.num}`;
                    const isChSpeaking =
                      speakingSection === chSectionId && isSpeaking;
                    return (
                      <Box
                        key={ch.num}
                        onClick={() => setActiveView("chapter_scripts")}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1,
                          py: 0.5,
                          borderRadius: 1.5,
                          cursor: "pointer",
                          transition: "background 0.2s",
                          background: isChSpeaking
                            ? alpha("#ec4899", 0.1)
                            : "transparent",
                          "&:hover": { background: alpha("#ec4899", 0.06) },
                        }}
                      >
                        <FiberManualRecord
                          sx={{
                            fontSize: 7,
                            color:
                              isChSpeaking || chapterScripts[ch.num]
                                ? "#10b981"
                                : alpha("#ec4899", 0.3),
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.65rem",
                            flex: 1,
                            color: isChSpeaking
                              ? "#ec4899"
                              : chapterScripts[ch.num]
                                ? "text.primary"
                                : "text.disabled",
                          }}
                          noWrap
                        >
                          {ch.num}. {ch.title.substring(0, 24)}
                          {ch.title.length > 24 ? "…" : ""}
                        </Typography>
                        {isChSpeaking && (
                          <AudioWaveform color="#ec4899" isActive bars={3} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Stack>
          </Box>

          {/* Stats */}
          {hasAnyContent && (
            <Box
              sx={{ p: 1.5, borderTop: `1px solid ${alpha("#ec4899", 0.1)}` }}
            >
              <Stack spacing={0.5}>
                {[
                  {
                    label: "Chapters scripted",
                    value: `${Object.values(chapterScripts).filter(Boolean).length}/${chapterTitles.length}`,
                  },
                  {
                    label: "Est. audio length",
                    value: `~${Math.ceil(
                      Object.values(chapterScripts)
                        .filter(Boolean)
                        .reduce((s, t) => s + t.split(" ").length, 0) / 150,
                    )} min`,
                  },
                ].map((s) => (
                  <Stack
                    key={s.label}
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.65rem" }}
                    >
                      {s.label}
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ fontSize: "0.65rem", color: "#ec4899" }}
                    >
                      {s.value}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: { xs: 2, md: 4 },
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: alpha("#ec4899", 0.2),
              borderRadius: 3,
            },
          }}
        >
          {!hasAnyContent && !isGenerating && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Box
                sx={{
                  width: 88,
                  height: 88,
                  borderRadius: 3,
                  mx: "auto",
                  mb: 3,
                  background: "linear-gradient(135deg, #ec4899, #8b5cf6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 32px rgba(236,72,153,0.35)",
                  animation: "emptyPulse 2s ease-in-out infinite",
                  "@keyframes emptyPulse": {
                    "0%, 100%": {
                      transform: "scale(1)",
                      boxShadow: "0 8px 32px rgba(236,72,153,0.35)",
                    },
                    "50%": {
                      transform: "scale(1.05)",
                      boxShadow: "0 12px 40px rgba(236,72,153,0.5)",
                    },
                  },
                }}
              >
                <Headphones sx={{ color: "white", fontSize: 44 }} />
              </Box>
              <Typography variant="h5" fontWeight={800} gutterBottom>
                Audiobook Script Generator
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 460, mx: "auto", mb: 3, lineHeight: 1.7 }}
              >
                Transform your book pipeline into a{" "}
                <strong>narration-ready audiobook script</strong> with voice
                direction, scene cues, and chapter-by-chapter narration.
                <br />
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ color: "#10b981", fontWeight: 600 }}
                >
                  📻 Includes live radio-style playback via your browser's
                  text-to-speech engine.
                </Typography>
              </Typography>
              <Paper
                sx={{
                  display: "inline-block",
                  px: 3,
                  py: 2,
                  borderRadius: 3,
                  background: alpha("#ec4899", 0.06),
                  border: `1px solid ${alpha("#ec4899", 0.2)}`,
                  mb: 3,
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography sx={{ fontSize: "1.5rem" }}>
                    {selectedVoice.icon}
                  </Typography>
                  <Box sx={{ textAlign: "left" }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{ color: "#ec4899" }}
                    >
                      {effectiveTone}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {effectiveLanguage} · {clampChapterCount(chapterCount)}{" "}
                      chapters · {selectedVoice.description}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                flexWrap="wrap"
                gap={1.5}
              >
                {generators.map((gen) => {
                  const has = !!pipelineContents?.[gen.id];
                  return (
                    <Chip
                      key={gen.id}
                      icon={
                        has ? (
                          <CheckCircle
                            sx={{
                              fontSize: "13px !important",
                              color: `${gen.color} !important`,
                            }}
                          />
                        ) : (
                          React.cloneElement(gen.icon, {
                            sx: { fontSize: "13px !important" },
                          })
                        )
                      }
                      label={gen.label}
                      size="small"
                      variant={has ? "filled" : "outlined"}
                      sx={{
                        background: has ? alpha(gen.color, 0.1) : "transparent",
                        color: has ? gen.color : "text.disabled",
                        borderColor: has
                          ? alpha(gen.color, 0.3)
                          : alpha(theme.palette.divider, 0.3),
                        fontWeight: has ? 700 : 400,
                        fontSize: "0.72rem",
                      }}
                    />
                  );
                })}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 1.5 }}
              >
                {Object.values(pipelineContents || {}).filter(Boolean).length}{" "}
                of 4 pipeline stages have content
              </Typography>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {(hasAnyContent || isGenerating) && (
            <Fade in>
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 3,
                    flexWrap: "wrap",
                  }}
                >
                  <Headphones sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                  >
                    {bookTitle}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    ›
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      color:
                        AUDIO_SECTIONS.find((s) => s.id === activeView)
                          ?.color || "#ec4899",
                    }}
                  >
                    {AUDIO_SECTIONS.find((s) => s.id === activeView)?.label ||
                      activeView}
                  </Typography>
                  <Chip
                    label={effectiveLanguage}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.6rem",
                      ml: 1,
                      bgcolor: alpha("#ec4899", 0.1),
                      color: "#ec4899",
                    }}
                  />
                  {isSpeaking && (
                    <Chip
                      label={isPaused ? "⏸ Paused" : "🔊 Narrating..."}
                      size="small"
                      onClick={isPaused ? resumeNarration : pauseNarration}
                      sx={{
                        height: 18,
                        fontSize: "0.6rem",
                        ml: 0.5,
                        cursor: "pointer",
                        bgcolor: isPaused
                          ? alpha("#f59e0b", 0.15)
                          : alpha("#10b981", 0.15),
                        color: isPaused ? "#f59e0b" : "#10b981",
                        border: `1px solid ${isPaused ? alpha("#f59e0b", 0.4) : alpha("#10b981", 0.4)}`,
                      }}
                    />
                  )}
                </Box>

                {renderPreview()}

                {generationComplete && activeView === "outro_script" && (
                  <Box
                    sx={{
                      mt: 4,
                      p: 3,
                      borderRadius: 2.5,
                      textAlign: "center",
                      background:
                        "linear-gradient(135deg, rgba(236,72,153,0.08), rgba(139,92,246,0.08))",
                      border: `1px solid ${alpha("#ec4899", 0.25)}`,
                    }}
                  >
                    <Headphones
                      sx={{ fontSize: 40, color: "#ec4899", mb: 1 }}
                    />
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      Audiobook Script Complete! 🎧
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {chapterTitles.length} chapter scripts · Voice & pacing
                      notes · Opening & closing narration
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      justifyContent="center"
                      flexWrap="wrap"
                      gap={1}
                    >
                      <Button
                        variant="contained"
                        startIcon={isRadioMode ? <Stop /> : <Radio />}
                        onClick={isRadioMode ? stopNarration : startRadioMode}
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 700,
                          background: isRadioMode
                            ? "#ef4444"
                            : "linear-gradient(135deg, #10b981, #059669)",
                        }}
                      >
                        {isRadioMode ? "Stop Radio" : "📻 Play All (Radio)"}
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={handleDownload}
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 700,
                          background:
                            "linear-gradient(135deg, #ec4899, #8b5cf6)",
                        }}
                      >
                        Download Script
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<ContentCopy />}
                        onClick={handleCopy}
                        sx={{
                          borderRadius: 2,
                          textTransform: "none",
                          borderColor: "#ec4899",
                          color: "#ec4899",
                        }}
                      >
                        Copy All
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Box>
            </Fade>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default BlogAIAudioBook;
